import {
  CommunicationChannel,
  MessagePayload,
  ServiceWorkerModule,
} from "../../../../libs/workers/types";

import { sendToAllClients } from "../../../../libs/workers/utils";
import { getDatasource } from "./datasource";
import { statsQueries } from "./queries";
import { performDataSync } from "./service";
import {
  StatsQueryError,
  StatsQueryErrors,
  StatsQueryTags,
  StatsQueryType,
} from "./types";
import { isStatsQuery, sendStatsQueryResult } from "./utils";

export const STATS_BROADCAST_CHANNEL_NAME = "worker-to-stats" as const;

const callQueryHandler = async <T>({
  client,
  event,
  datasource,
  worker,
}: {
  client: CommunicationChannel;
  event: ExtendableMessageEvent | MessageEvent<MessagePayload<T>>;
  datasource: ReturnType<typeof getDatasource>;
  worker: ServiceWorkerGlobalScope;
}) => {
  // console.log(
  //   `🔄 [${StatsQueryTags.STATS_MODULE_BASED}] Handling received stats query from client:`,
  //   event,
  //   event.data,
  //   event.data.type
  // );
  const { requestId, type, opts }: MessagePayload = event.data;
  if (!isStatsQuery(type)) {
    console.warn("❓ Unknown query type:", type, Object.keys(statsQueries));
    throw new StatsQueryError(
      StatsQueryErrors.INVALID_QUERY,
      `Unknown stats query type: ${type}`
    );
  } else if (datasource.getState().setupRequired) {
    throw new StatsQueryError(
      StatsQueryErrors.DATA_SOURCE_NOT_READY,
      "Datasource setup is required before processing queries. Wait for service worker to be ready.",
      { id: requestId, type, timestamp: event.timeStamp }
    );
  }

  const query = statsQueries[type];
  const result = query({ client, datasource, worker, ...event.data });
  if (opts?.verbose) {
    console.log(
      `🔄 [${StatsQueryTags.STATS_MODULE_BASED} | ${opts?.traceKey}] Result arrived:`,
      {
        result,
      }
    );
  }
  const responsePayload = result instanceof Promise ? await result : result;
  if (opts?.verbose) {
    console.log(
      `🔄 [${StatsQueryTags.STATS_MODULE_BASED} | ${opts?.traceKey}] Query processed:`,
      type,
      {
        responsePayload,
      }
    );
  }
  const requestRef = { id: requestId, type, timestamp: event.timeStamp };
  await sendStatsQueryResult(client, responsePayload, type, requestRef, opts);
  return result;
};

const onSync = async (
  event: SyncEvent | MessageEvent<MessagePayload> | ExtendableMessageEvent,
  datasource: ReturnType<typeof getDatasource>,
  worker: ServiceWorkerGlobalScope
) => {
  console.log("🔄 [${StatsQueryTags.STATS_MODULE_BASED}] Sync called:", event);
  const { accessToken } = datasource.getDirectusAuth();
  if (!accessToken) {
    console.warn("❌ No accessToken available for background sync");
    return;
  }
  await performDataSync(worker, accessToken, datasource);
};

const backgroundSyncsMap = {
  "initial-data-sync": {
    prepare: (
      client: CommunicationChannel,
      event: MessageEvent<MessagePayload> | ExtendableMessageEvent,
      datasource: ReturnType<typeof getDatasource>
    ) => {
      if (!event.data.auth?.accessToken) {
        console.warn(
          `${StatsQueryTags.STATS_MODULE_BASED} ❌ No directus accessToken provided for initial-data-sync. It is required to prepare the datasource.`,
          event.data.auth
        );
        return;
      }
      datasource.setDirectusAuth(event.data.auth.accessToken);
      console.log(
        `🔄 [${StatsQueryTags.STATS_MODULE_BASED}] initial-data-sync prepared`
      );
    },
    callback: (
      event: SyncEvent,
      datasource: ReturnType<typeof getDatasource>,
      worker: ServiceWorkerGlobalScope
    ) => onSync(event, datasource, worker),
  },
};

export const buildStatsModule = (
  worker: ServiceWorkerGlobalScope
): ServiceWorkerModule<StatsQueryType> => {
  const datasource = getDatasource();

  return {
    broadcastChannel: STATS_BROADCAST_CHANNEL_NAME,
    hasOwnListener: isStatsQuery,
    backgroundSyncs: Object.entries(backgroundSyncsMap).map(
      ([tag, configs]) => ({
        tag,
        callback: (event: SyncEvent) =>
          configs.callback(event, datasource, worker),
        prepare: (
          client: CommunicationChannel,
          event: MessageEvent<MessagePayload> | ExtendableMessageEvent
        ) => configs.prepare(client, event, datasource),
      })
    ),
    onLoad: async () => {
      console.log(
        `📦 [${StatsQueryTags.STATS_MODULE_BASED}] Loading stats module`
      );
      await datasource.initialize();
      console.log(
        `📦 [${StatsQueryTags.STATS_MODULE_BASED}] Datasource initialized during SW load. Timelines count:`,
        datasource.getTimeline().count()
      );
    },
    onSync: (
      client: CommunicationChannel,
      event: MessageEvent<MessagePayload> | ExtendableMessageEvent
    ) => {
      if (!event.data.auth?.accessToken) {
        console.warn(
          `❌ [${StatsQueryTags.STATS_MODULE_BASED}] No directus accessToken provided for sync. It is required to perform the sync.`
        );
        return;
      }

      return onSync(event, datasource, worker);
    },
    onActivate: async (event: ExtendableEvent) => {
      await datasource.initialize();
      console.log(
        `📦 [${StatsQueryTags.STATS_MODULE_BASED}] Datasource initialized during SW activation. Timelines count:`,
        datasource.getTimeline().count()
      );

      sendToAllClients(worker, {
        type: "READY",
        data: {
          timelinesCount: datasource.getTimeline().count(),
          timestamp: new Date().toISOString(),
        },
      });
    },
    onInitialize: async (
      client: CommunicationChannel,
      event: MessageEvent<MessagePayload> | ExtendableMessageEvent
    ) => {
      const currentState = datasource.getState();
      if (currentState.isRefreshing || currentState.setupRunning) {
        console.log(
          `⏳ [${StatsQueryTags.STATS_MODULE_BASED}] Initialization already in progress, skipping`
        );
        return;
      }
      console.log(
        `🔄 [${StatsQueryTags.STATS_MODULE_BASED}] Initialize triggered`
      );
      await datasource.initialize();
      if (datasource.getTimeline().count() === 0) {
        return false;
      } else {
        return true;
      }
    },
    isReady: (
      client: CommunicationChannel,
      event: ExtendableMessageEvent | MessageEvent<MessagePayload>
    ) => {
      const datasourceState = datasource.getState();
      const timelinesCount = datasource.getTimeline().count();
      console.log(
        `🔄 [${StatsQueryTags.STATS_MODULE_BASED}] CHECK_READY triggered`,
        datasourceState.setupRequired,
        timelinesCount
      );
      return !datasourceState.setupRequired && timelinesCount > 0;
    },
    onMessage: async (
      client: CommunicationChannel,
      event: MessageEvent<MessagePayload> | ExtendableMessageEvent
    ) => {
      await callQueryHandler({ client, event, datasource, worker });
    },
  };
};
